import { EventDispatcher } from "../Utils/event-dispatcher";
import { Organization } from "./../Entities/Organization";
import apiClient from "./api.client.service";
import AuthService from "./auth.service";

class OrganizationService extends EventDispatcher {
  private APIBaseUrl: string;
  private organizationCache: Organization = {} as Organization;
  private organizationDetailsCache: Organization = {} as Organization;
  private dispatcher = new EventDispatcher();
  private selectedTheme = {};
  constructor() {
    super();
    this.organizationCache = {} as Organization;
    this.organizationDetailsCache = {} as Organization;
    this.selectedTheme = {};
    this.APIBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "";

    AuthService.getInstance().subscribe(
      AuthService.EVENTS.USER_SIGNED_OUT,
      () => {
        this.clearOrganizationData();
      }
    );
  }

  async init() {
    await this.getOrganizationData();
  }

  async getOrganizationData(): Promise<Organization | null> {
    if (Object.keys(this.organizationCache).length)
      return this.organizationCache;

    try {
      const response = await apiClient.get(`${this.APIBaseUrl}/organization`);
      if (response.data) {
        this.dispatcher.dispatch("UPDATED_THEME");
        this.dispatcher.dispatch("ORGANIZATION_AVAILABLE");
        this.organizationCache = response.data as Organization;
        return response.data;
      } else {
        return null;
      }
    } catch (error) {
      console.error(
        "🚀 ~ OrganizationService ~ getOrganizationData ~ error:",
        error
      );
      return null;
    }
  }

  async fetchData() {
    const response = await apiClient.get(`${this.APIBaseUrl}/organization`);

    return response.data;
  }

  clearOrganizationData() {
    this.organizationCache = {} as Organization;
    this.organizationDetailsCache = {} as Organization;
    this.selectedTheme = {};
  }

  async getOrganizationDetails() {
    if (Object.keys(this.organizationDetailsCache).length)
      return this.organizationDetailsCache;

    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/organization/details`
      );
      if (response.data) {
        this.organizationDetailsCache = response.data as Organization;
        return response.data;
      } else {
        return null;
      }
    } catch (error) {
      console.error(
        "🚀 ~ OrganizationService ~ getOrganizationData ~ error:",
        error
      );
    }

    return this.organizationCache;
  }

  async getlogoUploadUrl(filename: string, fileType: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/organization/upload?filename=${filename}&fileType=${fileType}`
      );
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ OrganizationService ~ getlogoUploadUrl ~ error:",
        error
      );
    }
  }

  async saveLogo(logo: string) {
    try {
      const response = await apiClient.patch(
        `${this.APIBaseUrl}/organization/${logo}`
      );
      this.dispatcher.dispatch("ORGANIZATION_UPDATED", response.data);
      return response.data;
    } catch (error) {
      console.error("🚀 ~ OrganizationService ~ saveLogo ~ error:", error);
    }
  }

  async editOrganization(data: any) {
    try {
      const response = await apiClient.patch(
        `${this.APIBaseUrl}/admin/organization`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data.status === 200) {
        this.dispatcher.dispatch("ORGANIZATION_UPDATED", response.data);
        this.organizationCache = response.data;
        this.organizationDetailsCache = response.data;
      }
      return response.data;
    } catch (error) {
      console.error(
        "🚀 ~ OrganizationService ~ editOrganization ~ error:",
        error
      );
    }
  }

  async checkDomainAvailability(domain: string) {
    try {
      const response = await apiClient.get(
        `${this.APIBaseUrl}/admin/organization/check?domain=${domain}`
      );
      return response.data.available;
    } catch (error) {
      console.error(
        "🚀 ~ OrganizationService ~ checkDomainAvailability ~ error:",
        error
      );
    }
  }

  async changeTheme(theme: any) {
    try {
      this.selectedTheme = theme;
      this.dispatcher.dispatch("UPDATED_THEME");
      const payload = { theme };
      const response = await apiClient.patch(
        `${this.APIBaseUrl}/admin/organization`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      this.dispatcher.dispatch("UPDATED_THEME", response.data);
      return response.data;
    } catch (error) {
      console.error("🚀 ~ OrganizationService ~ changeTheme ~ error:", error);
    }
  }

  async getCurrentTheme() {
    if (this.selectedTheme) return this.selectedTheme;
    try {
      const organizationDetails = await this.getOrganizationDetails();
      return organizationDetails?.theme;
    } catch (error) {
      console.error("🚀 ~ OrganizationService ~ error:", error);
    }
  }

  async updateOrganization(data: Partial<Organization>) {
    try {
      const response = await apiClient.patch(
        `${this.APIBaseUrl}/organization/update/${data?._id}`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data) {
        this.dispatcher.dispatch("ORGANIZATION_UPDATED", response.data);
        this.organizationCache = response.data;
        this.organizationDetailsCache = response.data;
        return response.data;
      } else {
        console.error("Organization not updated");
      }
    } catch (error: any) {
      console.error(
        "🚀 ~ OrganizationService ~ updateOrganization ~ error:",
        error
      );
    }
  }

  subscribe(event: string, callback: (...args: any[]) => void) {
    this.dispatcher.subscribe(event, callback);
  }

  unsubscribe(event: string, callback: (...args: any[]) => void) {
    this.dispatcher.unsubscribe(event, callback);
  }
}

const organizationService = new OrganizationService();

export default organizationService;
